import React, { useContext } from 'react';
import { Box, Button, Text, Card, Avatar } from 'grommet';
import { Facebook, Next } from 'grommet-icons';
import Toast from '@components/Toast';
import ApiErrorDisplay from '@helpers/ApiErrorDisplay';
import { useFacebookLinking, FacebookVariant } from './useFacebook';
import FacebookDestinationPickerV3 from 'src/features/StreamingConfig/FacebookDestinationPickerV3';
import { FacebookSettingsModalV2 } from './FacebookSettingsModalV2';
import HowToAndErrorModal from '../HowToAndErrorStreamingModal';
import { LiveStreamSettingsModal } from '../LiveStreamSettingsModal';
import { FACEBOOK_HOW_TO_SET_UP_STREAMING } from '@constants/streamingHelpTexts';
import { TeamContext } from '@services/Session';
import SHDText from '@ui/Text/SHDText';
import SHDCallout from '@ui/Callout/SHDCallout';
import ApiError from '@helpers/ApiError';

interface Props {
  variant: FacebookVariant;
  truncateLength?: number | null;
  liveTeamId?: string | null;
}

const FacebookButton: React.FC<Props> = ({
  variant,
  truncateLength = null,
  liveTeamId = null,
}) => {
  const teamStore = useContext(TeamContext);
  const teamId = liveTeamId ? liveTeamId : teamStore.id;
  const isAdmin = teamStore.isTeamManager;

  const {
    onLinkFacebookClick,
    onUnlinkFacebookClick,
    handlers: { onClose, responseFacebook, onSave },
    ui: {
      setShowIntro,
      setShowHowTo,
      setShowLinking,
      setStepDestinationTitle,
      setDestinationGoBack,
      showIntro,
      showHowTo,
      showLinking,
      stepDestinationTitle,
      destinationGoBack,
      showLinkingSuccess,
      isLivestreamEligible,
    },
    publishFB,
    error,
    setError,
    fbScopes,
  } = useFacebookLinking({ variant, liveTeamId });

  const apiError = error && new ApiError(error);

  const formatTeamName = (name: string) =>
    truncateLength ? name.slice(0, truncateLength) + '...' : name;

  const renderTeamPageSkin = () => (
    <Box direction="row" justify="between" align="center">
      {publishFB ? (
        <a
          href={`https://www.facebook.com/${publishFB.destNodeId}`}
          target="_blank"
          rel="noreferrer"
        >
          <Button
            primary
            label={
              <SHDText style="footnote-bold">
                {formatTeamName(publishFB.destNodeName || '')}
              </SHDText>
            }
            color="#1877F2"
            icon={<Facebook color="white" size="small" />}
          />
        </a>
      ) : (
        isAdmin && (
          <Button
            secondary
            label={
              <SHDText color="#1877F2" style="footnote-bold">
                Connect Facebook
              </SHDText>
            }
            color="#1877F2"
            icon={<Facebook color="#1877F2" size="small" />}
            onClick={onLinkFacebookClick}
          />
        )
      )}
    </Box>
  );

  const renderTeamSettingsSkin = () => {
    const linkedChannelState = (
      <Box direction="row" align="center" gap="small" pad="small">
        {publishFB?.destNodeUrl ? (
          <Avatar src={publishFB.destNodeUrl} />
        ) : (
          <Avatar background="brand">
            {publishFB?.destNodeName?.slice(0, 2) || 'XY'}
          </Avatar>
        )}
        <Text>{`${publishFB?.destNodeName} (${publishFB?.destNodeType})`}</Text>
      </Box>
    );

    return (
      <Card background="white" pad="small">
        <Box gap="large" width="medium">
          {publishFB ? (
            <>
              <Text weight="bold">Streaming to:</Text>
              {linkedChannelState}
              <Button
                fill="horizontal"
                secondary
                color="status-error"
                icon={<Facebook color="#1877F2" />}
                label="Unlink Facebook"
                onClick={onUnlinkFacebookClick}
              />
            </>
          ) : (
            isAdmin && (
              <Box direction="row" align="center" gap="small" fill="horizontal">
                <Box width="medium">
                  <Button
                    fill="horizontal"
                    primary
                    color="white"
                    icon={<Facebook color="plain" />}
                    label="Link Facebook"
                    onClick={() => {
                      setShowIntro(true);
                    }}
                  />
                </Box>
              </Box>
            )
          )}
          {error && (
            <Toast
              label={error.toString()}
              background="status-error"
              duration={3000}
              onClose={() => setError(null)}
            />
          )}
        </Box>
      </Card>
    );
  };

  const renderLivestreamSetupSkin = () => (
    <SHDCallout round="medium">
      <Button onClick={onLinkFacebookClick} style={{ width: '100%' }}>
        <Box direction="row" align="center" justify="between" pad="xsmall">
          <Box
            width="48px"
            height="48px"
            round="full"
            align="center"
            justify="center"
            style={{ backgroundColor: publishFB ? '#1877F2' : '#B6B6B6' }}
          >
            <Facebook size="24px" color={publishFB ? 'white' : '#FFFFFF'} />
          </Box>
          <Box flex style={{ marginLeft: '16px' }}>
            <SHDText style="subhead-semibold">Facebook</SHDText>
            <SHDText
              style="footnote"
              color={publishFB ? '#7C7C7C' : 'gray-500'}
            >
              {publishFB
                ? formatTeamName(publishFB.destNodeName || '')
                : 'Link account'}
            </SHDText>
          </Box>
          <Box flex={{ shrink: 0, grow: 0 }}>
            <Next size="14px" color={publishFB ? 'black-alpha-30' : 'black'} />
          </Box>
        </Box>
      </Button>
    </SHDCallout>
  );

  const renderButton = () => {
    switch (variant) {
      case 'team-page':
        return renderTeamPageSkin();
      case 'team-settings':
        return renderTeamSettingsSkin();
      case 'livestream-setup':
        return renderLivestreamSetupSkin();
      default:
        break;
    }
  };

  return (
    <>
      {renderButton()}

      {showIntro && (
        <FacebookSettingsModalV2
          onLinkFacebookClick={onLinkFacebookClick}
          onCloseIntroModal={() => setShowIntro(false)}
          setShowLinkingModal={setShowLinking}
          setShowHowToSetUpModal={() => setShowHowTo(true)}
          fbScopes={fbScopes}
          teamId={teamId}
          responseFacebook={responseFacebook}
        />
      )}

      {showHowTo && (
        <HowToAndErrorModal
          headerText="LINK FACEBOOK"
          title="How to set up Facebook livestreaming"
          subtitle=""
          onClose={() => setShowHowTo(false)}
          isError={false}
          htmlContent={FACEBOOK_HOW_TO_SET_UP_STREAMING}
        />
      )}

      {showLinking && (
        <LiveStreamSettingsModal
          headerText="LINK FACEBOOK"
          title={stepDestinationTitle}
          onCloseModal={onClose}
          goBack={destinationGoBack}
        >
          <FacebookDestinationPickerV3
            setStepDestinationTitle={setStepDestinationTitle}
            setDestinationGoBack={setDestinationGoBack}
            teamId={teamId}
            onClose={() => {
              onClose();
              setShowIntro(false);
            }}
            onLinked={onSave}
            error={apiError}
            showLinkingSuccess={showLinkingSuccess}
            isLivestreamEligible={isLivestreamEligible}
            destination={publishFB}
            linkError={error}
          />
        </LiveStreamSettingsModal>
      )}

      {error && <ApiErrorDisplay error={apiError} />}
    </>
  );
};

export default FacebookButton;
